<script lang="ts" setup>
import { computed, ref } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import {
  OrganizationUserAssociationType,
  UserSwitchOrganizationDocument,
} from '@/gql/graphql';
import { useUserStore } from '@/store/user';

// --- Props for the component ---
interface Props {
  association: OrganizationUserAssociationType;
  show: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['orgActivated', 'close']);
const userStore = useUserStore();

// --- Computed Properties ---
const isActive = computed(() => {
  return (
    userStore.activeOrgAssociation?.organization?.id ===
    props.association.organization?.id
  );
});
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Switch Active Organization ---
const mutationInProgress = ref(false);
const mutationShowSuccess = ref(false);
const mutationError = ref<string | null>(null);

const {
  mutate: userSwitchOrganizationMut,
  onDone: onUserSwitchOrganizationDone,
  onError: onUserSwitchOrganizationError,
} = useMutation(UserSwitchOrganizationDocument);

onUserSwitchOrganizationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.userSwitchOrganization) {
    console.debug('Organization switched:', res.data.userSwitchOrganization);
    mutationShowSuccess.value = true;
    emits('orgActivated', res.data.userSwitchOrganization.id);
    setTimeout(() => {
      mutationShowSuccess.value = false;
    }, 1000);
  } else {
    console.error('Organization switch failed:', res.data);
    mutationError.value = 'Organization switch failed: ' + res.data;
  }
});

onUserSwitchOrganizationError((err) => {
  console.error('Error switching organization:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers
function switchOrganization() {
  // Switch active organization
  mutationInProgress.value = true;
  mutationError.value = null;

  // Validate
  if (!props.association.organization?.id) {
    console.error('No organization to switch to');
    mutationInProgress.value = false;
    mutationError.value = 'No organization to switch to';
    return;
  }

  // Execute mutation
  console.log('Switching organization:', props.association.organization.id);
  userSwitchOrganizationMut({
    organizationId: props.association.organization.id,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.association.organization"
    class="orgActivateModal"
    v-model="show"
    @hide="handleCloseModal"
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'building']" class="me-2" />
      <strong>{{ props.association.organization.name }}</strong>
    </template>

    <!-- Content -->
    <div>
      <!-- Organization Not Active: Activate -->
      <div v-if="!isActive">
        <p>
          You are currently not active in this organization. Click the button
          below to switch your active organization context to
          <strong>{{ props.association.organization.name }}</strong
          >.
        </p>
      </div>
      <!-- Organization Already Active -->
      <div v-else>
        <p>
          You are currently active in this organization. Your role is
          <strong>{{ props.association.userRole }}</strong
          >. You may now access organization resources.
        </p>
      </div>
    </div>
    <!-- Mutation Error Messages -->
    <BAlert v-model="mutationError" variant="danger" dismissible class="mt-3">
      {{ mutationError }}
    </BAlert>

    <!-- Footer -->
    <template #footer>
      <b-button variant="secondary" @click="handleCloseModal">
        <span v-if="!isActive">Cancel</span>
        <span v-else>Close</span>
      </b-button>
      <b-button
        v-if="!isActive"
        variant="primary"
        :disabled="mutationInProgress || mutationShowSuccess"
        @click="switchOrganization()"
      >
        <span v-if="mutationInProgress">
          Activating Organization
          <b-spinner small label=""></b-spinner>
        </span>
        <span v-else-if="mutationShowSuccess">
          Organization Activated
          <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
        </span>
        <span v-else> Activate Organization </span>
      </b-button>
    </template>
  </b-modal>
</template>
